import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, MapPin } from "@phosphor-icons/react";

// module-level cache so the meta is fetched once per session
let _metaCache = null;
export async function fetchLocationMeta() {
  if (_metaCache) return _metaCache;
  const { data } = await api.get("/projects/meta/locations");
  _metaCache = data;
  return data;
}

export function locationLabel(loc) {
  if (!loc) return "";
  const bits = [];
  const seen = new Set();
  ["site_name", "city_town", "district", "region", "state", "country"].forEach((k) => {
    const v = (loc[k] || "").trim();
    if (v && !seen.has(v.toLowerCase())) { bits.push(v); seen.add(v.toLowerCase()); }
  });
  return bits.slice(0, 4).join(", ");
}

export function primaryLocation(project) {
  const locs = project?.locations || [];
  if (!locs.length) return null;
  return locs.find((l) => l.is_primary) || locs[0];
}

export function LocationChip({ project }) {
  const locs = project?.locations || [];
  if (!locs.length) return null;
  const label = locationLabel(primaryLocation(project));
  const extra = locs.length - 1;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 px-2 py-0.5 text-[11px] font-medium text-teal-800"
      title={locs.map(locationLabel).join(" | ")}
      data-testid={`project-location-chip-${project.id}`}
    >
      <MapPin size={11} weight="fill" />
      <span className="truncate max-w-[220px]">{label}</span>
      {extra > 0 && <span className="text-teal-600">+{extra}</span>}
    </span>
  );
}

const BLANK_LOC = {
  country: "Somalia", state: "", region: "", district: "",
  city_town: "", site_name: "", address: "", gps_lat: "", gps_lng: "", is_primary: false,
};

export default function ProjectLocationEditor({ locations, setLocations }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => { fetchLocationMeta().then(setMeta).catch(() => {}); }, []);

  const add = () => setLocations([...(locations || []), { ...BLANK_LOC, is_primary: (locations || []).length === 0 }]);
  const remove = (i) => setLocations(locations.filter((_, idx) => idx !== i));
  const update = (i, patch) => {
    const c = [...locations];
    c[i] = { ...c[i], ...patch };
    setLocations(c);
  };
  const setPrimary = (i) => setLocations(locations.map((l, idx) => ({ ...l, is_primary: idx === i })));

  const somStates = meta?.somalia || [];
  const stateObj = (name) => somStates.find((s) => s.state === name);
  const regionObj = (stateName, regionName) => (stateObj(stateName)?.regions || []).find((r) => r.region === regionName);

  return (
    <div className="space-y-3" data-testid="project-location-editor">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <MapPin size={14} /> Project locations
        </Label>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-location-button">
          <Plus size={12} className="mr-1" /> Add location
        </Button>
      </div>
      {(locations || []).length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-center text-xs text-slate-500">
          No locations yet — add country, state/region, district, city and site details.
        </div>
      )}
      {(locations || []).map((loc, i) => {
        const isSomalia = (loc.country || "Somalia") === "Somalia";
        return (
          <div key={loc.id || i} className="grid grid-cols-12 gap-2 rounded-md border border-teal-100 bg-teal-50/40 p-3" data-testid={`location-row-${i}`}>
            <div className="col-span-6 md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Country *</Label>
              <Select value={loc.country || "Somalia"}
                onValueChange={(v) => update(i, { country: v, state: "", region: "", district: "" })}>
                <SelectTrigger className="h-9" data-testid={`location-country-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.countries || ["Somalia"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isSomalia ? (
              <>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Federal member state</Label>
                  <Select value={loc.state || undefined}
                    onValueChange={(v) => update(i, { state: v, region: "", district: "" })}>
                    <SelectTrigger className="h-9" data-testid={`location-state-${i}`}><SelectValue placeholder="Select state…" /></SelectTrigger>
                    <SelectContent>
                      {somStates.map((s) => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Region</Label>
                  <Select value={loc.region || undefined} disabled={!loc.state}
                    onValueChange={(v) => update(i, { region: v, district: "" })}>
                    <SelectTrigger className="h-9" data-testid={`location-region-${i}`}><SelectValue placeholder={loc.state ? "Select region…" : "Pick state first"} /></SelectTrigger>
                    <SelectContent>
                      {(stateObj(loc.state)?.regions || []).map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">District</Label>
                  <Select value={loc.district || undefined} disabled={!loc.region}
                    onValueChange={(v) => update(i, { district: v })}>
                    <SelectTrigger className="h-9" data-testid={`location-district-${i}`}><SelectValue placeholder={loc.region ? "Select district…" : "Pick region first"} /></SelectTrigger>
                    <SelectContent>
                      {(regionObj(loc.state, loc.region)?.districts || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">State / Province</Label>
                  <Input className="h-9" value={loc.state || ""} onChange={(e) => update(i, { state: e.target.value })} data-testid={`location-state-intl-${i}`} />
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Region</Label>
                  <Input className="h-9" value={loc.region || ""} onChange={(e) => update(i, { region: e.target.value })} />
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">District / County</Label>
                  <Input className="h-9" value={loc.district || ""} onChange={(e) => update(i, { district: e.target.value })} />
                </div>
              </>
            )}
            <div className="col-span-6 md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">City / Town</Label>
              <Input className="h-9" value={loc.city_town || ""} onChange={(e) => update(i, { city_town: e.target.value })} data-testid={`location-city-${i}`} />
            </div>
            <div className="col-span-6 md:col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Site name</Label>
              <Input className="h-9" placeholder="e.g. KM4 Interchange Site" value={loc.site_name || ""} onChange={(e) => update(i, { site_name: e.target.value })} data-testid={`location-site-${i}`} />
            </div>
            <div className="col-span-12 md:col-span-5">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Address</Label>
              <Input className="h-9" value={loc.address || ""} onChange={(e) => update(i, { address: e.target.value })} />
            </div>
            <div className="col-span-6 md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">GPS latitude</Label>
              <Input className="h-9" type="number" step="0.000001" placeholder="2.0469" value={loc.gps_lat ?? ""}
                onChange={(e) => update(i, { gps_lat: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`location-lat-${i}`} />
            </div>
            <div className="col-span-6 md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">GPS longitude</Label>
              <Input className="h-9" type="number" step="0.000001" placeholder="45.3182" value={loc.gps_lng ?? ""}
                onChange={(e) => update(i, { gps_lng: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`location-lng-${i}`} />
            </div>
            <label className="col-span-5 md:col-span-5 flex items-center gap-2 text-xs text-slate-700">
              <input type="radio" name="primary-location" checked={!!loc.is_primary} onChange={() => setPrimary(i)} data-testid={`location-primary-${i}`} />
              <span>Primary location</span>
            </label>
            <div className="col-span-1 flex items-end justify-end">
              <Button type="button" variant="outline" className="h-9" onClick={() => remove(i)} aria-label="Remove location" data-testid={`location-remove-${i}`}>
                <Trash size={12} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
